import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { concat, fn } from "@ember/helper";
import { on } from "@ember/modifier";
import { action } from "@ember/object";
import didUpdate from "@ember/render-modifiers/modifiers/did-update";
import { next } from "@ember/runloop";
import { service } from "@ember/service";
import { ajax } from "discourse/lib/ajax";
import { CREATE_TOPIC } from "discourse/models/composer";
import { and, eq, not } from "discourse/truth-helpers";
import DButton from "discourse/ui-kit/d-button";
import DSelect from "discourse/ui-kit/d-select";
import dConcatClass from "discourse/ui-kit/helpers/d-concat-class";
import { i18n } from "discourse-i18n";
import CommunityFeedbackCard from "./community-feedback-card";
import CommunityFeedbackGameFilter from "./community-feedback-game-filter";

const SORTS = ["latest", "hot"];
const FEEDBACK_STATUSES = ["collecting", "adopted"];
const WISH_STATUSES = [
  "open_for_claim",
  "claimed",
  "in_progress",
  "completed",
];
const TAG_FILTER_ENABLED = false;

export default class CommunityFeedbackGallery extends Component {
  @service a11y;
  @service composer;
  @service router;

  @tracked section;
  @tracked items = [];
  @tracked loading = true;
  @tracked error;
  @tracked hasMore = false;

  requestId = 0;

  constructor() {
    super(...arguments);
    void this.load();
  }

  willDestroy() {
    super.willDestroy(...arguments);
    this.requestId++;
  }

  get outletArgs() {
    return this.args.outletArgs || {};
  }

  get itemType() {
    return this.args.itemType === "wish" ? "wish" : "feedback";
  }

  get isWish() {
    return this.itemType === "wish";
  }

  get i18nPrefix() {
    return this.isWish ? "community_feedback.wishes" : "community_feedback";
  }

  get statuses() {
    return this.isWish ? WISH_STATUSES : FEEDBACK_STATUSES;
  }

  get sort() {
    return SORTS.includes(this.outletArgs.sort)
      ? this.outletArgs.sort
      : "latest";
  }

  get status() {
    return this.statuses.includes(this.outletArgs.status)
      ? this.outletArgs.status
      : "all";
  }

  get game() {
    return this.outletArgs.game || "all";
  }

  get selectedGameName() {
    if (this.game === "all") {
      return i18n("community_feedback.filters.all_games");
    }

    return this.section?.games?.find((game) => game.key === this.game)?.name;
  }

  get tag() {
    return this.outletArgs.tag || "";
  }

  get page() {
    const page = Number.parseInt(this.outletArgs.page, 10);
    return Number.isInteger(page) && page >= 0 ? page : 0;
  }

  get endpoint() {
    return this.isWish
      ? "/community-feedback/wishes.json"
      : "/community-feedback/items.json";
  }

  get routeName() {
    return this.isWish ? "community-wishes" : "community-media-feedback";
  }

  async load() {
    const requestId = ++this.requestId;
    this.loading = true;
    this.error = null;
    this.items = [];
    this.hasMore = false;

    try {
      const responses = [];
      for (let page = 0; page <= this.page; page++) {
        responses.push(
          await ajax(this.endpoint, {
            data: {
              sort: this.sort,
              status: this.status,
              game: this.game,
              tag: this.tag,
              page,
            },
          })
        );
      }

      if (requestId !== this.requestId) {
        return;
      }

      this.section = responses[0]?.section;
      this.items = responses.flatMap((response) => response.items);
      this.hasMore = responses.at(-1)?.meta.has_more || false;
      this.a11y.announce(
        i18n(`${this.i18nPrefix}.result_count`, {
          count: this.items.length,
        }),
        "polite"
      );
    } catch {
      if (requestId === this.requestId) {
        this.error = i18n(`${this.i18nPrefix}.load_error`);
        this.a11y.announce(this.error, "assertive");
      }
    } finally {
      if (requestId === this.requestId) {
        this.loading = false;
      }
    }
  }

  @action
  reload() {
    void this.load();
  }

  transition(queryParams) {
    this.router.transitionTo(this.routeName, {
      queryParams: {
        sort: this.sort,
        status: this.status,
        game: this.game,
        tag: this.tag,
        page: 0,
        ...queryParams,
      },
    });
  }

  @action
  selectSort(sort) {
    this.transition({ sort, status: "all" });
  }

  @action
  selectStatus(status) {
    this.transition({ status });
  }

  @action
  selectGame(game) {
    this.transition({ game: game || "all" });
  }

  @action
  selectTag(tag) {
    this.transition({ tag: tag || "" });
  }

  @action
  loadMore() {
    this.transition({ page: this.page + 1 });
  }

  @action
  async publish() {
    const categoryId = this.section.category_id;
    await this.composer.open({
      action: CREATE_TOPIC,
      draftKey: this.composer.topicDraftKey,
      draftSequence: 0,
      categoryId,
      topicCategoryId: categoryId,
      readOnlyCategoryId: categoryId,
    });
    this.composer.model.setProperties({
      categoryId,
      communityFeedbackPublishing: true,
      communityFeedbackItemType: this.itemType,
      communityFeedbackPublishLabel: this.isWish
        ? "community_feedback.wishes.publish_action"
        : "community_feedback.publish_action",
      communityFeedbackGames: this.section.games,
      communityFeedbackGameKey: null,
      communityFeedbackCustomFields: {},
    });
    next(this, () => {
      if (this.composer.model?.communityFeedbackItemType === this.itemType) {
        this.composer.disableSubmit = true;
      }
    });
  }

  <template>
    <section
      class={{dConcatClass "community-feedback" (if this.isWish "--wish")}}
      aria-labelledby="community-feedback-title"
      {{didUpdate
        this.reload
        this.itemType
        this.sort
        this.status
        this.game
        this.tag
        this.page
      }}
      ...attributes
    >
      <header class="community-feedback__header">
        <div class="community-feedback__heading">
          <h1 id="community-feedback-title" class="community-feedback__title">
            {{this.section.name}}
          </h1>
          <span class="community-feedback__eyebrow">
            {{i18n (concat this.i18nPrefix ".eyebrow")}}
          </span>
        </div>
        <div class="community-feedback__intro">
          <p class="community-feedback__description">
            {{i18n (concat this.i18nPrefix ".description")}}
          </p>
          <span class="community-feedback__mascot" aria-hidden="true"></span>
          <span
            class="community-feedback__intro-art"
            aria-hidden="true"
          ></span>
        </div>
      </header>

      <div class="community-feedback__panel">
        <CommunityFeedbackGameFilter
          @games={{this.section.games}}
          @selected={{this.game}}
          @onSelect={{this.selectGame}}
        />

        <div class="community-feedback__toolbar">
          <strong class="community-feedback__selected-game">
            {{this.selectedGameName}}
          </strong>
          <div class="community-feedback__toolbar-actions">
            <div
              class="community-feedback__filter-group"
              role="group"
              aria-label={{i18n "community_feedback.sort_filter"}}
            >
              {{#each SORTS as |sort|}}
                <button
                  type="button"
                  class="community-feedback__filter"
                  aria-pressed={{if
                    (and (eq this.sort sort) (eq this.status "all"))
                    "true"
                    "false"
                  }}
                  {{on "click" (fn this.selectSort sort)}}
                >
                  {{i18n (concat "community_feedback.filters." sort)}}
                </button>
              {{/each}}
              {{#each this.statuses as |status|}}
                <button
                  type="button"
                  class="community-feedback__filter"
                  aria-pressed={{if (eq this.status status) "true" "false"}}
                  {{on "click" (fn this.selectStatus status)}}
                >
                  {{i18n (concat "community_feedback.statuses." status)}}
                </button>
              {{/each}}
            </div>
            {{#if TAG_FILTER_ENABLED}}
              <DSelect
                class="community-feedback__select"
                @value={{this.tag}}
                @nonePlaceholder={{i18n
                  "community_feedback.filters.all_tags"
                }}
                @onChange={{this.selectTag}}
                aria-label={{i18n "community_feedback.tag_filter"}}
                as |select|
              >
                {{#each this.section.tags as |tag|}}
                  <select.Option @value={{tag}}>{{tag}}</select.Option>
                {{/each}}
              </DSelect>
            {{/if}}
          </div>
        </div>

        {{#if this.loading}}
          <p class="community-feedback__message">
            {{i18n (concat this.i18nPrefix ".loading")}}
          </p>
        {{else if this.error}}
          <div class="community-feedback__message --error">
            <p>{{this.error}}</p>
            <DButton
              class="btn-default"
              @action={{this.reload}}
              @label="community_feedback.retry"
            />
          </div>
        {{else if this.items.length}}
          <ul class="community-feedback__list">
            {{#each this.items as |item|}}
              <li class="community-feedback__list-item">
                <CommunityFeedbackCard @item={{item}} @isWish={{this.isWish}} />
              </li>
            {{/each}}
          </ul>
        {{else if (and (not this.loading) (not this.error))}}
          <div class="community-feedback__empty">
            <h2>{{i18n (concat this.i18nPrefix ".empty_title")}}</h2>
            <p>{{i18n (concat this.i18nPrefix ".empty_description")}}</p>
          </div>
        {{/if}}

        {{#if this.hasMore}}
          <DButton
            class="btn-default community-feedback__load-more"
            @action={{this.loadMore}}
            @label="community_feedback.load_more"
          />
        {{/if}}
      </div>
    </section>
  </template>
}
